import { motion, useInView } from "framer-motion"
import React, { useEffect, lazy, Suspense } from "react"
import MonterItem from "./MonterItem"
import { cleanText } from "../../util/stringUtil"
import LikeButton from "../UIComponents/LikeButton"
import GlobalsItem from "./GlobalsItem"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import Button from "../UIComponents/Button"
const HotSpotMap = lazy(() => import("../HotspotMap/HotSpotMap"))

const variants = {
    open: {
        transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
    closed: {
        transition: { staggerChildren: 0.1, staggerDirection: -1 },
    },
}
const imgVariants = {
    open: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            duration: 0.3,
        },
    },
    closed: {
        opacity: 0,
        scale: 0.98,
        y: 5,
        transition: {
            duration: 1,
        },
    },
}

const MonterContent = ({ modalOpen, data, isFavorite, hideLike = false }) => {
    const [loadImage, setLoadImage] = React.useState(false)
    const [isZoomed, setIsZoomed] = React.useState(false)
    const transformRef = React.useRef(null)
    const mapRef = React.useRef(null)
    const mapIsInView = useInView(mapRef, { once: true, amount: "all" })
    const MAX_ZOOM = 1.8

    useEffect(() => {
        if (mapIsInView && transformRef.current) {
            const element = document.getElementById(data.monternummer)
            if (element) {
                transformRef.current.zoomToElement(element, MAX_ZOOM)
                setIsZoomed(true)
            }
        }
    }, [mapIsInView, data.monternummer])

    const handleZoomOutButtonClick = () => {
        if (!transformRef.current) return

        if (isZoomed) {
            transformRef.current.resetTransform()
            setIsZoomed(false)
        }
    }

    const handleZoomInButtonClick = () => {
        if (!transformRef.current) return

        if (!isZoomed) {
            const element = document.getElementById(data.monternummer)
            if (element) {
                transformRef.current.zoomToElement(element, MAX_ZOOM) // justera för mer/mindre zoom vid dubbelklick
                setIsZoomed(true)
            }
        }
    }

    const Controls = () => {
        return (
            <div className=" flex gap-2 bottom-4 absolute right-4 z-10">
                <Button
                    onClick={handleZoomOutButtonClick}
                    disabled={!isZoomed}
                    variant={`round-button`}
                    classes={!isZoomed ? "opacity-50 pointer-events-none" : ""}
                >
                    -
                </Button>
                <Button
                    onClick={handleZoomInButtonClick}
                    disabled={isZoomed}
                    variant={`round-button`}
                    classes={isZoomed ? "opacity-50 pointer-events-none" : ""}
                >
                    +
                </Button>
            </div>
        )
    }

    return (
        <motion.div
            className="p-4 bg-color-1 divide-y overflow-hidden divide-neutral-300 dark:divide-neutral-700"
            variants={variants}
            initial="closed"
            animate={modalOpen ? "open" : "closed"}
            onAnimationComplete={() => setLoadImage(true)}
        >
            <MonterItem className={"pb-4"}>
                <div className="relative">
                    {data.logotyp && loadImage ? (
                        <motion.img
                            variants={imgVariants}
                            initial="closed"
                            animate={modalOpen ? "open" : "closed"}
                            className="mb-4 size-24 object-contain mx-auto  bg-white rounded-lg border border-neutral-300  shadow-lg"
                            src={data["logotyp"]}
                            alt="logo for company"
                        />
                    ) : (
                        <div className="mb-4 size-24 opacity-0 aspect-square mx-auto rounded-lg bg-white border border-neutral-300 shadow-md"></div>
                    )}
                    <h2 className="text-xl text-center font-bold fg-color-1">
                        {data["organisation_namn"]}
                    </h2>
                    {!hideLike && (
                        <span className="absolute top-0 right-0">
                            <LikeButton
                                data={data}
                                isFavorite={isFavorite}
                                size={40}
                            />
                        </span>
                    )}
                </div>
            </MonterItem>
            <MonterItem className={"pt-4 pb-6 "}>
                <h3 className="font-semibold mb-2 fg-color-2">Monterplats</h3>
                <span className="py-1 px-2 font-semibold bg-primary-500 text-white rounded text-sm">
                    {data["monternummer"]}
                </span>
            </MonterItem>
            {data["vi-erbjuder-studenter"].length > 0 && (
                <MonterItem className={"pt-4 pb-6 "}>
                    <h3 className="font-semibold mb-2 fg-color-2 ">Tjänster</h3>
                    <div className="flex flex-wrap gap-1">
                        {data["vi-erbjuder-studenter"].map((str, index) => {
                            return (
                                <span key={index} className="tag">
                                    {str}
                                </span>
                            )
                        })}
                    </div>
                </MonterItem>
            )}
            {data["vi-soker-studenter-inom"].length > 0 && (
                <MonterItem className={"pt-4 pb-6 "}>
                    <h3 className="font-semibold mb-2 fg-color-2">Branscher</h3>
                    <div className="flex flex-wrap gap-1">
                        {data["vi-soker-studenter-inom"].map((str, index) => {
                            return (
                                <span key={index} className="tag">
                                    {str}
                                </span>
                            )
                        })}
                    </div>
                </MonterItem>
            )}
            {data.yrkestitlar && (
                <MonterItem className={"pt-4 pb-6 fg-color-2"}>
                    <h3 className="font-semibold mb-2">Yrkestitlar</h3>
                    <p className="leading-6 fg-color-3">{data.yrkestitlar}</p>
                </MonterItem>
            )}
            {data.verksamhetsorter && (
                <MonterItem className={"pt-4 pb-6 fg-color-2"}>
                    <h3 className="font-semibold mb-2">Verksamhetsorter</h3>
                    <p className="leading-6 fg-color-3">
                        {data.verksamhetsorter}
                    </p>
                </MonterItem>
            )}
            {data["antal-anstallda"] > 0 && (
                <MonterItem className={"pt-4 pb-6 fg-color-2"}>
                    <h3 className="font-semibold mb-2">Antal anställda</h3>
                    <p className="leading-6 fg-color-3">
                        {data["antal-anstallda"]} anställda
                    </p>
                </MonterItem>
            )}
            {data["katalogtext"] && (
                <MonterItem className={"pt-4 pb-6 fg-color-2"}>
                    <h3 className="font-semibold mb-2">Beskrivning</h3>
                    <p className="text-sm leading-6 text-wrap overflow-hidden fg-color-3 whitespace-pre-wrap">
                        {cleanText(data.katalogtext)}
                    </p>
                </MonterItem>
            )}
            {data["globala-malen"].length > 0 && (
                <MonterItem className={"pt-4 pb-6 fg-color-2"}>
                    <h3 className="font-semibold mb-2">Globala mål</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                        {data["globala-malen"].map((str, index) => (
                            <GlobalsItem key={index} str={str} />
                        ))}
                    </div>
                </MonterItem>
            )}
            <MonterItem className={"pt-4 pb-6 fg-color-2"}>
                <h3 className="font-semibold mb-2">Karta</h3>
                <div className="relative w-full max-w-96 md:max-w-80 aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-primary-400 to-primary-500">
                    <Controls />
                    <motion.div
                        ref={mapRef}
                        animate={{
                            scale: isZoomed ? MAX_ZOOM : 1,
                        }}
                        transition={{
                            type: "spring",
                            stiffness: 260,
                            damping: 20,
                            duration: 0.5,
                        }}
                        className="w-full pointer-events-none"
                    >
                        <TransformWrapper
                            ref={transformRef}
                            panning={{ disabled: true }}
                            wheel={{ disabled: true }}
                            pinch={{ disabled: true }}
                            centerOnInit={true}
                            centerZoomedOut={true}
                            initialScale={1}
                            minScale={0.5}
                            maxScale={4}
                            limitToBounds={false}
                        >
                            <TransformComponent
                                contentClass="rounded-lg overflow-hidden"
                                wrapperClass="w-full flex justify-center items-center"
                            >
                                <Suspense>
                                    <HotSpotMap
                                        monternummer={data.monternummer}
                                    />
                                </Suspense>
                            </TransformComponent>
                        </TransformWrapper>
                    </motion.div>
                </div>
            </MonterItem>
        </motion.div>
    )
}

export default MonterContent
